import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Alert, App as AntdApp, Breadcrumb, Button, ConfigProvider, Layout, Menu, Result, Spin } from 'antd';
import {
  AppstoreOutlined,
  DashboardOutlined,
  PlusCircleOutlined,
  UnorderedListOutlined,
  CrownOutlined,
  TeamOutlined,
  BarChartOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  KeyOutlined,
  TrophyOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  UserOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import Dashboard from './pages/Dashboard';
import NewCase from './pages/NewCase';
import CaseList from './pages/CaseList';
import CaseDetail from './pages/CaseDetail';
import Subscription from './pages/Subscription';
import TenantSettings from './pages/TenantSettings';
import AccountSettings from './pages/AccountSettings';
import TenantStats from './pages/TenantStats';
import CaseHistory from './pages/CaseHistory';
import ContentPage from './pages/ContentPage';
import DrugList from './pages/DrugList';
import ActivationManagement from './pages/ActivationManagement';
import PersonalAccount from './pages/PersonalAccount';
import TaskMarketplace from './pages/TaskMarketplace';
import AdminPanel from './pages/AdminPanel';
import AdminLogin from './pages/AdminLogin';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import PendingApproval from './pages/PendingApproval';
import InviteAccept from './pages/InviteAccept';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AgentProvider, useAgent } from './contexts/AgentContext';
import { ResponsiveProvider, useResponsive } from './contexts/ResponsiveContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserMenu from './components/UserMenu';
import TenantSelector from './components/TenantSelector';
import QuotaAlert from './components/QuotaAlert';
import AgentDrawer from './components/AgentDrawer';
import MobileAccountPanel from './components/mobile/MobileAccountPanel';
import MobileAppBar from './components/mobile/MobileAppBar';
import MobileBottomNav from './components/mobile/MobileBottomNav';
import { loadCovariateDefinitions } from './services/api';
import { useSiteBranding } from './hooks/useSiteBranding';
import { buildSidebarMenuItems, type SidebarSection } from './utils/sidebarNav';

const { Header, Sider, Content, Footer } = Layout;

type MobileNavGroup = 'workbench' | 'business' | 'profile' | 'settings';
type MobileBackBehavior = 'none' | 'history' | string;

interface AppRouteMeta {
  label: string;
  groupLabel: string;
  mobileTitle: string;
  mobileNavGroup: MobileNavGroup;
  mobileBackBehavior: MobileBackBehavior;
  mobileHeaderActions: Array<'account'>;
}

const MOBILE_GROUP_BY_SECTION: Record<string, MobileNavGroup> = {
  'section-business': 'business',
  'section-personal': 'profile',
  'section-org': 'settings',
};

const buildAppNavSections = (canManageTenant: boolean): SidebarSection[] => [
  {
    key: 'section-business',
    label: '业务',
    icon: <AppstoreOutlined />,
    children: [
      { key: '/new-case', icon: <PlusCircleOutlined />, label: '新建病例' },
      { key: '/cases', icon: <UnorderedListOutlined />, label: '病例列表' },
      { key: '/history', icon: <HistoryOutlined />, label: '历史记录' },
      { key: '/drugs', icon: <MedicineBoxOutlined />, label: '支持药物' },
    ],
  },
  {
    key: 'section-personal',
    label: '个人中心',
    icon: <UserOutlined />,
    children: [
      { key: '/personal-account', icon: <UserOutlined />, label: '个人账户' },
      { key: '/tasks', icon: <TrophyOutlined />, label: '悬赏任务' },
      { key: '/subscription', icon: <CrownOutlined />, label: '套餐管理' },
      { key: '/activation', icon: <KeyOutlined />, label: '激活码管理' },
    ],
  },
  {
    key: 'section-org',
    label: canManageTenant ? '机构设置' : '个人设置',
    icon: <SettingOutlined />,
    children: [
      {
        key: '/settings/tenant-stats',
        icon: <BarChartOutlined />,
        label: canManageTenant ? '业务统计' : '我的业务统计',
      },
      ...(canManageTenant
        ? [{ key: '/settings/tenant', icon: <TeamOutlined />, label: '机构设置' }]
        : []),
      { key: '/settings/account', icon: <SettingOutlined />, label: '账号设置' },
    ],
  },
];

const buildSectionByRoute = (sections: SidebarSection[]) => sections.reduce<Record<string, string>>((acc, section) => {
  section.children.forEach((item) => {
    acc[item.key] = section.key;
  });
  return acc;
}, {});

const buildRouteMeta = (sections: SidebarSection[]) => sections.reduce<Record<string, AppRouteMeta>>(
  (acc, section) => {
    section.children.forEach((item) => {
      acc[item.key] = {
        label: item.label,
        groupLabel: section.label,
        mobileTitle: item.label,
        mobileNavGroup: MOBILE_GROUP_BY_SECTION[section.key],
        mobileBackBehavior: 'none',
        mobileHeaderActions: ['account'],
      };
    });
    return acc;
  },
  {
    '/': {
      label: '工作台',
      groupLabel: '首页',
      mobileTitle: '工作台',
      mobileNavGroup: 'workbench',
      mobileBackBehavior: 'none',
      mobileHeaderActions: ['account'],
    },
    '/cases/:id': {
      label: '病例详情',
      groupLabel: '业务',
      mobileTitle: '病例详情',
      mobileNavGroup: 'business',
      mobileBackBehavior: 'history',
      mobileHeaderActions: ['account'],
    },
  },
);

const APP_SHELL_STYLES = `
  .workspace-shell {
    background: #F5F7FA;
  }

  .workspace-shell__sider {
    box-shadow: 10px 0 28px rgba(10, 25, 47, 0.14);
    z-index: 10;
  }

  .workspace-shell__sider .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, #001529 0%, #0A2744 100%);
  }

  .workspace-shell__menu.ant-menu-dark {
    background: transparent;
    border-inline-end: none;
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-item {
    height: 44px;
    line-height: 44px;
    margin: 4px 12px;
    width: calc(100% - 24px);
    border-radius: 12px;
    color: rgba(255, 255, 255, 0.88);
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-item:hover {
    color: #FFFFFF;
    background: rgba(24, 144, 255, 0.18);
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-item-selected {
    color: #FFFFFF;
    background: linear-gradient(135deg, rgba(24, 144, 255, 0.34) 0%, rgba(24, 144, 255, 0.2) 100%);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-item-divider {
    background: rgba(255, 255, 255, 0.08);
    margin: 12px 18px 6px;
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-submenu {
    margin-top: 2px;
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-submenu-title {
    height: 44px;
    line-height: 44px;
    margin: 4px 12px;
    width: calc(100% - 24px);
    border-radius: 12px;
    color: rgba(255, 255, 255, 0.7);
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-submenu-title:hover {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.08);
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-submenu-open > .ant-menu-submenu-title,
  .workspace-shell__menu.ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: #FFFFFF;
    background: rgba(24, 144, 255, 0.14);
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-sub {
    background: transparent !important;
    padding-bottom: 6px;
  }

  .workspace-shell__menu.ant-menu-dark .ant-menu-sub .ant-menu-item {
    margin-left: 24px;
    width: calc(100% - 36px);
    color: rgba(255, 255, 255, 0.72);
  }

  .workspace-shell__menu-popup.ant-menu-submenu-popup > .ant-menu {
    min-width: 200px;
    padding: 6px;
    border-radius: 14px;
    background: #0A2744;
  }

  .workspace-shell__menu-popup.ant-menu-submenu-popup .ant-menu-item {
    border-radius: 12px;
  }

  .workspace-shell__header {
    background: #FFFFFF;
    height: 64px;
    padding: 0 24px;
    border-bottom: 1px solid #E7EDF4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .workspace-shell__header-left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1;
  }

  .workspace-shell__header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0;
  }

  .workspace-shell__header-divider {
    width: 1px;
    height: 22px;
    background: #E7EDF4;
    flex-shrink: 0;
  }

  .workspace-shell__breadcrumb {
    color: #8C8C8C;
    font-size: 13px;
    line-height: 1;
  }

  .workspace-shell__breadcrumb .ant-breadcrumb-link,
  .workspace-shell__breadcrumb .ant-breadcrumb-separator {
    color: #8C8C8C;
  }

  .workspace-shell__breadcrumb li:last-child .ant-breadcrumb-link {
    color: #262626;
    font-weight: 600;
  }

  .workspace-shell__content {
    background: #F5F7FA;
    padding: 24px;
  }

  .workspace-shell__content--mobile {
    padding: 0 0 calc(96px + env(safe-area-inset-bottom));
  }

  .workspace-shell__mobile-alerts {
    padding: 12px 16px 0;
  }

  .workspace-shell__workspace {
    display: flex;
    align-items: flex-start;
    min-height: calc(100vh - 196px);
    gap: 0;
    transition: gap 0.28s ease;
  }

  .workspace-shell__workspace--agent-open {
    gap: 16px;
  }

  .workspace-shell__workspace--mobile {
    display: block;
    min-height: auto;
  }

  .workspace-shell__main {
    flex: 1;
    min-width: 0;
  }

  .workspace-shell__main--mobile {
    padding: 12px 16px 0;
  }

  .workspace-shell__footer {
    text-align: center;
    color: #8C8C8C;
    background: transparent;
    padding: 10px 24px 18px;
    font-size: 12px;
  }

  @media (max-width: 1200px) {
    .workspace-shell__content {
      padding: 20px;
    }
  }

  @media (max-width: 1024px) {
    .workspace-shell__header {
      padding: 0 16px;
    }

    .workspace-shell__content {
      padding: 16px;
    }

    .workspace-shell__content--mobile {
      padding: 0 0 calc(96px + env(safe-area-inset-bottom));
    }

    .workspace-shell__breadcrumb {
      display: none;
    }

    .workspace-shell__header-actions {
      gap: 8px;
    }
  }
`;

const normalizeSelectedKey = (pathname: string) => {
  if (pathname.startsWith('/cases/')) return '/cases';
  return pathname;
};

const getRouteMetaKey = (pathname: string) => {
  if (pathname.startsWith('/cases/')) return '/cases/:id';
  return pathname;
};

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { drawerOpen, updateContext } = useAgent();
  const { isMobileShell } = useResponsive();
  const { currentTenant, canManageCurrentTenant, currentTenantReadOnly, loading: tenantLoading, refreshTenants } = useTenant();
  const [siderCollapsed, setSiderCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1200;
  });
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [brandIconFailed, setBrandIconFailed] = useState(false);
  const {
    siteName,
    slogan: sidebarSlogan,
    copyright,
    userWorkspaceLogoUrl,
    userWorkspaceIconUrl,
    brandMonogram,
  } = useSiteBranding();

  const navigation = useMemo(() => {
    const sections = buildAppNavSections(canManageCurrentTenant);
    return {
      sectionKeys: sections.map((section) => section.key),
      menuItems: buildSidebarMenuItems(
        sections,
        { key: '/', icon: <DashboardOutlined />, label: '工作台' },
        'workspace-shell__menu-popup',
      ),
      sectionByRoute: buildSectionByRoute(sections),
      routeMeta: buildRouteMeta(sections),
    };
  }, [canManageCurrentTenant]);

  useEffect(() => {
    loadCovariateDefinitions();
  }, []);

  useEffect(() => {
    setBrandLogoFailed(false);
  }, [userWorkspaceLogoUrl]);

  useEffect(() => {
    setBrandIconFailed(false);
  }, [userWorkspaceIconUrl]);

  useEffect(() => {
    updateContext({ currentPath: location.pathname });
  }, [location.pathname, updateContext]);

  useEffect(() => {
    if (currentTenantReadOnly && location.pathname === '/new-case') {
      navigate('/cases', { replace: true });
    }
  }, [currentTenantReadOnly, location.pathname, navigate]);

  const selectedKey = normalizeSelectedKey(location.pathname);
  const activeMeta = navigation.routeMeta[getRouteMetaKey(location.pathname)] || navigation.routeMeta['/'];
  const activeSectionKey = navigation.sectionByRoute[selectedKey];
  const headerBreadcrumbItemsRuntime = useMemo(() => {
    const items = [{ title: '首页' }];
    if (activeMeta.groupLabel !== '首页') items.push({ title: activeMeta.groupLabel });
    items.push({ title: activeMeta.label });
    return items;
  }, [activeMeta.groupLabel, activeMeta.label]);
  const mobileNavItemsRuntime = useMemo(() => ([
    { key: 'workbench', label: '工作台', icon: <DashboardOutlined />, target: '/' },
    { key: 'business', label: '业务', icon: <AppstoreOutlined />, target: '/new-case' },
    { key: 'profile', label: '个人中心', icon: <UserOutlined />, target: '/personal-account' },
    { key: 'settings', label: '设置', icon: <SettingOutlined />, target: '/settings/account' },
  ]), []);

  useEffect(() => {
    if (siderCollapsed) {
      setOpenKeys([]);
      return;
    }
    setOpenKeys(activeSectionKey ? [activeSectionKey] : []);
  }, [activeSectionKey, siderCollapsed]);

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === '/new-case') {
      navigate('/new-case', { state: { reset: Date.now() } });
      return;
    }
    navigate(key);
  };

  const handleMobileBack = () => {
    if (activeMeta.mobileBackBehavior === 'history') {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
      navigate('/cases');
      return;
    }
    if (activeMeta.mobileBackBehavior && activeMeta.mobileBackBehavior !== 'none') {
      navigate(activeMeta.mobileBackBehavior);
    }
  };

  const mobileHeaderAction = activeMeta.mobileHeaderActions.includes('account') ? (
    <Button
      type="text"
      icon={<EllipsisOutlined style={{ fontSize: 20 }} />}
      onClick={() => setMobileAccountOpen(true)}
      style={{
        width: 40,
        height: 40,
        borderRadius: 12,
        color: '#061529',
      }}
    />
  ) : null;

  if (tenantLoading) {
    return (
      <ProtectedRoute>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Spin size="large" />
          <div style={{ color: '#667085', fontSize: 14 }}>正在加载工作区...</div>
        </div>
      </ProtectedRoute>
    );
  }

  const appRoutes = (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/new-case" element={<NewCase />} />
      <Route path="/cases" element={<CaseList />} />
      <Route path="/cases/:id" element={<CaseDetail />} />
      <Route path="/drugs" element={<DrugList />} />
      <Route path="/history" element={<CaseHistory />} />
      <Route path="/tasks" element={<TaskMarketplace />} />
      <Route path="/subscription" element={<Subscription />} />
      <Route path="/personal-account" element={<PersonalAccount />} />
      <Route path="/activation" element={<ActivationManagement />} />
      <Route
        path="/settings/tenant"
        element={tenantLoading
          ? <Spin size="large" />
          : canManageCurrentTenant
            ? <TenantSettings />
            : <Navigate to="/settings/account" replace />}
      />
      <Route path="/settings/tenant-stats" element={<TenantStats />} />
      <Route path="/settings/account" element={<AccountSettings />} />
    </Routes>
  );

  if (!tenantLoading && !currentTenant) {
    return (
      <ProtectedRoute>
        <Result
          status="info"
          title="机构加入申请待审批"
          subTitle="机构管理员批准后即可进入工作区。"
          extra={<Button type="primary" onClick={() => refreshTenants()}>刷新状态</Button>}
        />
      </ProtectedRoute>
    );
  }

  if (isMobileShell) {
    return (
      <ProtectedRoute>
        <Layout className="workspace-shell" style={{ minHeight: '100vh' }}>
          <style>{APP_SHELL_STYLES}</style>

          <MobileAppBar
            title={activeMeta.mobileTitle}
            showBack={activeMeta.mobileBackBehavior !== 'none'}
            onBack={handleMobileBack}
            rightAction={mobileHeaderAction}
          />

          <Content className="workspace-shell__content workspace-shell__content--mobile">
            <div className="workspace-shell__mobile-alerts">
              <QuotaAlert />
            </div>

            <div className="workspace-shell__workspace workspace-shell__workspace--mobile">
              <div className="workspace-shell__main workspace-shell__main--mobile">
                {appRoutes}
              </div>
            </div>
          </Content>

          <MobileBottomNav
            items={mobileNavItemsRuntime}
            activeKey={activeMeta.mobileNavGroup}
            onChange={(key) => {
              const target = mobileNavItemsRuntime.find((item) => item.key === key)?.target;
              if (target) navigate(target);
            }}
            hidden={drawerOpen}
          />

          <MobileAccountPanel
            open={mobileAccountOpen}
            onClose={() => setMobileAccountOpen(false)}
          />

          <AgentDrawer />
        </Layout>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Layout className="workspace-shell" style={{ minHeight: '100vh' }}>
        <style>{APP_SHELL_STYLES}</style>
        <Sider
          collapsed={siderCollapsed}
          trigger={null}
          width={240}
          collapsedWidth={72}
          theme="dark"
          breakpoint="xl"
          onBreakpoint={(broken) => setSiderCollapsed(broken)}
          className="workspace-shell__sider"
        >
          <div
            style={{
              minHeight: 86,
              padding: siderCollapsed ? '0 12px' : '16px 20px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: siderCollapsed ? 'center' : 'flex-start',
              justifyContent: siderCollapsed ? 'center' : 'flex-start',
            }}
          >
            {siderCollapsed ? (
              userWorkspaceIconUrl && !brandIconFailed ? (
                <img
                  src={userWorkspaceIconUrl}
                  alt={`${siteName} icon`}
                  style={{ width: 34, height: 34, objectFit: 'contain', display: 'block' }}
                  onError={() => setBrandIconFailed(true)}
                />
              ) : (
                <span style={{ color: '#FFFFFF', fontWeight: 700 }}>{brandMonogram}</span>
              )
            ) : (
              <div style={{ minWidth: 0, width: '100%' }}>
                {userWorkspaceLogoUrl && !brandLogoFailed ? (
                  <img
                    src={userWorkspaceLogoUrl}
                    alt={siteName}
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      height: 30,
                      objectFit: 'contain',
                      objectPosition: 'left center',
                    }}
                    onError={() => setBrandLogoFailed(true)}
                  />
                ) : (
                  <div style={{ color: '#FFFFFF', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{siteName}</div>
                )}
                <div style={{ color: 'rgba(255,255,255,0.56)', fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>
                  {sidebarSlogan}
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '10px 0 12px' }}>
            <Menu
              mode="inline"
              theme="dark"
              selectedKeys={[selectedKey]}
              openKeys={siderCollapsed ? [] : openKeys}
              onClick={handleMenuClick}
              onOpenChange={(keys) =>
                setOpenKeys((keys as string[]).filter((key) => navigation.sectionKeys.includes(key)).slice(-1))
              }
              items={navigation.menuItems}
              className="workspace-shell__menu"
            />
          </div>
        </Sider>

        <Layout>
          <Header className="workspace-shell__header">
            <div className="workspace-shell__header-left">
              <Button
                type="text"
                icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setSiderCollapsed((prev) => !prev)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#F8FAFD',
                  border: '1px solid #E7EDF4',
                  color: '#445266',
                }}
              />
              <div className="workspace-shell__header-divider" />
              <Breadcrumb className="workspace-shell__breadcrumb" items={headerBreadcrumbItemsRuntime} />
            </div>
            <div className="workspace-shell__header-actions">
              <TenantSelector />
              <UserMenu />
            </div>
          </Header>

          <Content className="workspace-shell__content">
            {currentTenantReadOnly && (
              <Alert
                type="warning"
                showIcon
                banner
                message="当前租户已暂停：病例、历史记录和既往报告可继续查看；新建、修改、计算、生成报告和计费操作已停用。"
                style={{ marginBottom: 12 }}
              />
            )}
            <QuotaAlert />

            <div className={`workspace-shell__workspace${drawerOpen ? ' workspace-shell__workspace--agent-open' : ''}`}>
              <div className="workspace-shell__main">
                {appRoutes}
              </div>
              <AgentDrawer />
            </div>
          </Content>

          <Footer className="workspace-shell__footer">{copyright}</Footer>
        </Layout>
      </Layout>
    </ProtectedRoute>
  );
};

const HomeRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) return <LandingPage />;

  return <AppLayout />;
};

const AppInner: React.FC = () => {
  const { themeConfig } = useTheme();

  return (
    <ConfigProvider locale={zhCN} theme={themeConfig}>
      <AntdApp>
        <Router>
          <AuthProvider>
            <TenantProvider>
              <ResponsiveProvider>
                <AgentProvider>
                  <Routes>
                    <Route path="/landing" element={<LandingPage />} />
                    <Route path="/pages/:slug" element={<ContentPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/pending-approval" element={<ProtectedRoute><PendingApproval /></ProtectedRoute>} />
                    <Route path="/invitations/accept" element={<InviteAccept />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminPanel />} />
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/*" element={<AppLayout />} />
                  </Routes>
                </AgentProvider>
              </ResponsiveProvider>
            </TenantProvider>
          </AuthProvider>
        </Router>
      </AntdApp>
    </ConfigProvider>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppInner />
  </ThemeProvider>
);

export default App;
